import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import moment from 'moment';

import * as actions from './actions';
import { Wrapper, Container, Button } from '../../components';
import { Header, HeadingContainer } from '../ChallengeListPage/styles';
import { InputAndSearchSection, DateLabel, InputSection } from './styles';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableData,
  SortArrowImage,
} from '../../components/cmsRankList/style';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';

export default React.memo(() => {
  const [items, setItems] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [isAscending, setIsAscending] = useState(false);

  const date = new Date(),
    year = date.getFullYear(),
    month = date.getMonth();

  const [minDate, setMinDate] = useState(moment(new Date(year, month, 1)).format('YYYY-MM-DD'));
  const [maxDate, setMaxDate] = useState(moment(new Date()).format('YYYY-MM-DD'));
  const [companyName, setCompanyName] = useState('');

  const { getStatistics } = useActions(actions);

  useEffect(() => {
    getStatistics({ minDate, maxDate, companyName });
  }, [getStatistics]);

  let { loading, statistics } = useSelector((state) => state.statistics);

  useEffect(() => {
    setItems(statistics);
  }, [statistics]);

  const sort = (array, field, isAscending) => {
    let sortableItems = [...array];
    return sortableItems.sort((a, b) => {
      let x, y;
      if (field === 'company') {
        x = a['name'];
        y = b['name'];
      } else if (field === 'creditsUsed') {
        x = a['payment']['creditsUsed'] || 0;
        y = b['payment']['creditsUsed'] || 0;
      } else if (field === 'creditsBought') {
        x = a['payment']['creditsBought'] || 0;
        y = b['payment']['creditsBought'] || 0;
      } else {
        x = a['customerLog']['totalLogin'] || 0;
        y = b['customerLog']['totalLogin'] || 0;
      }
      if (x < y) {
        return isAscending ? -1 : 1;
      }
      if (x > y) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortListBy = (field) => {
    let updatedList = sort(statistics, field, field !== sortBy ? false : isAscending);
    setItems(updatedList);
    if (field !== sortBy) {
      setIsAscending(true);
    } else {
      setIsAscending(!isAscending);
    }
    setSortBy(field);
  };

  const setTodaysDate = () => {
    let currentDate = moment(new Date()).format('YYYY-MM-DD');
    setMinDate(currentDate);
    setMaxDate(currentDate);
    getStatistics({
      minDate: currentDate,
      maxDate: currentDate,
      companyName,
    });
  };

  const renderTopSection = () => {
    return (
      <InputAndSearchSection>
        <InputSection>
          <DateLabel>Start Date: </DateLabel>
          <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <DateLabel>End Date: </DateLabel>
          <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <Input
            type="text"
            placeholder="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <Button
            padding="5px 15px"
            margin="0 0 0 20px"
            primaryBlue
            onClick={() => getStatistics({ minDate, maxDate, companyName })}
          >
            Search
          </Button>
        </InputSection>
        <InputSection>
          <Button padding="5px 15px" margin="0" primaryBlue onClick={() => setTodaysDate()}>
            Today
          </Button>
        </InputSection>
      </InputAndSearchSection>
    );
  };

  const renderTableHeading = () => {
    return (
      <TableRow isHeading>
        <TableHeading>
          Company
          <SortArrowImage
            onClick={() => sortListBy('company')}
            alt="arrow"
            src={sortBy === 'company' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading>
          Credits Used
          <SortArrowImage
            onClick={() => sortListBy('creditsUsed')}
            alt="arrow"
            src={sortBy === 'creditsUsed' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading>
          Credits Bought{' '}
          <SortArrowImage
            onClick={() => sortListBy('creditsBought')}
            alt="arrow"
            src={sortBy === 'creditsBought' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading>
          Login
          <SortArrowImage
            onClick={() => sortListBy('login')}
            alt="arrow"
            src={sortBy === 'login' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
      </TableRow>
    );
  };

  const renderUserDetailsTable = (userDetails) => {
    if (!userDetails) return null;
    return (
      <Table>
        <TableHead>
          <TableRow isHeading>
            <TableHeading>Name</TableHeading>
            <TableHeading>Date</TableHeading>
          </TableRow>
        </TableHead>
        <TableBody>
          {userDetails.map((item, index) => {
            return (
              <TableRow key={index}>
                <TableData>{item.name}</TableData>
                <TableData>{moment(item.lastActive).format('DD/MM/YYYY')}</TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const renderTableBody = () => {
    return items.map((item, index) => {
      return (
        <>
          <TableRow key={index}>
            <TableData>{item.name}</TableData>
            <TableData>{item.payment.creditsUsed ? item.payment.creditsUsed : 0}</TableData>
            <TableData>{item.payment.creditsBought ? item.payment.creditsBought : 0}</TableData>
            <TableData>
              {item.customerLog._id ? item.customerLog.totalLogin : 0}{' '}
              {item.customerLog.totalLogin > 0 ? (
                <i
                  style={{ cursor: 'pointer' }}
                  className={`pl-1 fa ${index} ${
                    isCollapsed ? 'fa-plus' : activeIndex === index ? 'fa-minus' : 'fa-plus'
                  }`}
                  onClick={() => {
                    setActiveIndex(index);
                    setIsCollapsed(!isCollapsed);
                  }}
                />
              ) : (
                ''
              )}
            </TableData>
          </TableRow>
          {!isCollapsed && activeIndex === index ? (
            <TableRow>
              <TableData colSpan="4">{renderUserDetailsTable(item.customerLog.details)}</TableData>
            </TableRow>
          ) : null}
        </>
      );
    });
  };

  return (
    <Wrapper padding="60px 0 0 0" height="100%">
      <Container column>
        <HeadingContainer>
          <Header>Company Statistics</Header>
        </HeadingContainer>
        {renderTopSection()}
        {items && items.length && !loading ? (
          <Table>
            <TableHead>{renderTableHeading()}</TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        ) : loading ? (
          'Loading'
        ) : (
          'No data found'
        )}
      </Container>
    </Wrapper>
  );
});
